// ─────────────────────────────────────────────────────────────────
// Client-side storage helper.
//
// PATH B — presigned-URL flow. The browser never gets S3 credentials;
// it asks our /api/storage endpoints for a short-lived signed URL,
// then uploads/downloads directly to S3.
//
// This preserves the pattern currently in use for large-file uploads
// (deal-room diligence docs, library uploads) while giving us
// server-side auth control over who can upload where.
//
// USAGE:
//   // Old:
//   await db.storage.from('proposal-library').upload(path, file);
//
//   // New:
//   await storage.upload('proposal-library', path, file);
//
//   // Old:
//   const { data } = await db.storage.from('deal-room').createSignedUrl(path, 3600);
//
//   // New:
//   const url = await storage.signedDownloadUrl('deal-room', path);
// ─────────────────────────────────────────────────────────────────

import { api } from './api.js';

async function fetchWithProgress(url, method, body, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total, pct: e.loaded / e.total });
      };
    }
    xhr.onload  = () => xhr.status >= 200 && xhr.status < 300
      ? resolve({ status: xhr.status })
      : reject(new Error(`upload failed: HTTP ${xhr.status}`));
    // A failed preflight (CORS) or DNS/TLS failure surfaces here with no
    // status. Name the host so the admin can tell "wrong endpoint" from
    // "flaky network" without opening devtools.
    xhr.onerror = () => {
      let host = '';
      try { host = new URL(url).host; } catch {}
      reject(new Error(`upload network error${host ? ` (PUT to ${host} was blocked — check S3 CORS / S3_ENDPOINT)` : ''}`));
    };
    xhr.send(body);
  });
}

export const storage = {
  /**
   * Upload a file (Blob or File) to a bucket + path. Returns
   * { path } on success or { error } on failure.
   *
   * @param {string} bucket        — logical name (e.g. 'proposal-library')
   * @param {string} path          — key inside the bucket
   * @param {Blob|File} file
   * @param {(progress:{loaded,total,pct})=>void} [onProgress]
   */
  async upload(bucket, path, file, onProgress) {
    // 1. Ask the server for a presigned PUT URL
    const { data, error } = await api.post('/api/storage/upload-url', {
      bucket,
      path,
      content_type: file.type || 'application/octet-stream',
      size:         file.size,
    });
    if (error) return { error };
    if (!data?.url) return { error: 'no signed url returned' };

    // 2. PUT the bytes directly to S3
    try {
      await fetchWithProgress(data.url, 'PUT', file, file.type || 'application/octet-stream', onProgress);
      return { path };
    } catch (e) {
      return { error: e.message };
    }
  },

  /**
   * Get a short-lived signed download URL. Server-side auth check
   * happens in /api/storage/download-url.
   */
  async signedDownloadUrl(bucket, path, expiresIn = 3600) {
    const { data, error } = await api.post('/api/storage/download-url', {
      bucket, path, expires_in: expiresIn,
    });
    if (error) return null;
    return data?.url || null;
  },

  /**
   * Download a file into a Blob. Convenience wrapper on top of
   * signedDownloadUrl + fetch.
   */
  async download(bucket, path) {
    const url = await this.signedDownloadUrl(bucket, path);
    if (!url) return { error: 'could not obtain signed URL' };
    const resp = await fetch(url);
    if (!resp.ok) return { error: `HTTP ${resp.status}` };
    return { blob: await resp.blob() };
  },

  async remove(bucket, path) {
    return await api.delete(`/api/storage/object?${new URLSearchParams({ bucket, path })}`);
  },
};
